import { Link } from 'react-router-dom'
import {
  useListIngestIntakesQuery,
  type IngestMappingConfigSummary
} from '@axilon-apps/domains-ontology-ui-state'
import { cn } from '../../lib/utils'
import { usedByCell } from '../IngestPage/configs/config-view'
import { intakesTabPath } from '../IngestPage/intakes/intake-view'
import { tabularConfigPath } from '../TabularIngestJobPage/route'
import { formatWhen } from './util/formatMeta'

/**
 * What an archived version's page says: whether the whole config or only this
 * version is archived, and which intakes still point at it. Those intakes are
 * not changed by archiving, so their new files fail until they are repointed.
 */
export function ArchivedBanner({
  name,
  config,
  latestActive,
  navState
}: {
  name: string
  config: IngestMappingConfigSummary
  /** The newest active version; absent when every version is archived. */
  latestActive?: IngestMappingConfigSummary
  navState?: unknown
}) {
  const intakesQuery = useListIngestIntakesQuery()
  const usedBy = intakesQuery.data ? usedByCell(name, intakesQuery.data.intakes) : []
  // With an active version left, only intakes pinned to this one are stuck.
  const affected = latestActive
    ? usedBy.filter((entry) => entry.pinnedVersion === config.version)
    : usedBy
  const when = config.archived_at ? ` ${formatWhen(config.archived_at)}` : ''

  return (
    <div
      role="status"
      data-testid="archived-banner"
      className={cn(
        'rounded-lg border px-3 py-2 text-xs leading-5',
        affected.length ? 'border-st-warn/40 bg-st-warn-bg' : 'border-line bg-muted'
      )}
    >
      {latestActive ? (
        <p>
          v{config.version} is archived{when}. The current version is{' '}
          <Link
            className="underline"
            to={tabularConfigPath(name, latestActive.version)}
            state={navState}
          >
            v{latestActive.version}
          </Link>
          .
        </p>
      ) : (
        <p>
          Every version of {name} is archived{when}. It is hidden from pickers and cannot start new
          runs. Recorded runs and the rows in <code className="text-11">urn:db:{name}</code> are
          kept.
        </p>
      )}
      {affected.length ? (
        <p className="mt-1 text-st-warn">
          {affected.length === 1
            ? '1 intake still points'
            : `${affected.length} intakes still point`}{' '}
          at {latestActive ? 'this version' : 'it'}:{' '}
          {affected.map((entry, index) => (
            <span key={entry.intakeId}>
              {index > 0 ? ', ' : null}
              <Link className="font-medium underline" to={intakesTabPath(entry.intakeId)}>
                {entry.intakeName}
              </Link>
              {entry.pinnedVersion && !latestActive ? ` (pinned to v${entry.pinnedVersion})` : null}
            </span>
          ))}
          .{' '}
          {affected.length === 1
            ? 'Its new files fail until it points at an active config or is turned off.'
            : 'Their new files fail until each one points at an active config or is turned off.'}
        </p>
      ) : null}
    </div>
  )
}
